import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  HBars,
  Input,
  LoadingState,
  StatTile,
  Tabs,
} from '@/components/ui';
import { FileButton } from '@/components/FileButton';
import { useBranch } from '@/features/branch/branch-context';
import { formatRM } from '@/features/billing/money';
import { api } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PageContainer } from '@/layouts/PageContainer';
import { todayKl } from '@/pages/calendar/time';
import dash from '@/pages/DashboardPage.module.css';
import { ReportDispatchPanel } from './ReportDispatch';
import styles from './parity.module.css';
import { METHOD_LABEL, type PaymentMethod } from '@/features/billing/types';
/** GET /reports/group row (RL-1827: one visit count, booked %, app adoption). */
interface GroupRow {
  branch: string;
  /** Consultations started in the month; the service mix counts the same. */
  visits: number;
  bookedVisits: number;
  bookedPct: number;
  revenueSen: number;
  panelSharePct: number;
  avgWaitMinutes: number | null;
  patientsSeen: number;
  appPatients: number;
  /** appPatients / patientsSeen; null with no visits. */
  appAdoptionPct: number | null;
  panelOutstandingSen: number;
  patientOutstandingSen: number;
}
interface GroupReport {
  month: string;
  rows: GroupRow[];
  serviceMix: { service: string; visits: number; sharePct: number }[];
}
interface Daily {
  day: string;
  visits: number;
  netSen: number;
  openBills: number;
  openBalanceSen: number;
  cashDifferenceSen: number;
  shifts: number;
  byMethod: { method: string; sen: number }[];
  refundsSen: number;
}
interface RevenueRow {
  name: string;
  monthSen: number;
  ytdSen: number;
}
interface Revenue {
  month: string;
  totalMonthSen: number;
  totalYtdSen: number;
  byPayer: RevenueRow[];
  byService: RevenueRow[];
  byDoctor: RevenueRow[];
  byBranch: RevenueRow[];
}
interface ClaimsRow {
  payer: string;
  claims: number;
  rejected: number;
  avgDaysToPay: number | null;
  termsDays: number;
  writtenOffSen: number;
  topReason: string | null;
}
interface Stock {
  valueSen: number;
  expiredBatches: number;
  writeOffSen: number;
  unitsDispensed: number;
  fastest: { name: string; qty: number }[];
  slowest: { name: string; qty: number }[];
}
type ReportTab =
  'daily' | 'revenue' | 'claims' | 'stock' | 'quality' | 'antibiotics';
function useReport<T>(path: string, period: string) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['operational-reports', path, period, selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api.get<T>(`/reports/${path}${period}`, { signal }).then((r) => r.data),
  });
}
export function ReportsPage({ group }: { group: boolean }) {
  const { t } = useTranslation();
  const [tab, setTab] = useUrlState<ReportTab>('tab', 'daily', [
    'daily',
    'revenue',
    'claims',
    'stock',
    'quality',
    'antibiotics',
  ]);
  const [day, setDay] = useUrlState('day', todayKl());
  const [month, setMonth] = useUrlState('month', todayKl().slice(0, 7));
  return (
    <PageContainer>
      {!group && (
        <Tabs
          variant="bar"
          label={t('reports.title')}
          value={tab}
          onChange={setTab}
          items={(
            [
              'daily',
              'revenue',
              'claims',
              'stock',
              'quality',
              'antibiotics',
            ] as ReportTab[]
          ).map((value) => ({ value, label: t(`reports.${value}`) }))}
        />
      )}
      {(group ||
        tab === 'revenue' ||
        tab === 'stock' ||
        tab === 'antibiotics') && (
        <label className={styles.periodBar}>
          <span>{t('books.month')}</span>

          <Input
            className={styles.headerControl}

            type="month"

            required

            value={month}

            onChange={(e) => setMonth(e.target.value)}
          />
        </label>
      )}
      {!group && tab === 'daily' && (
        <label className={styles.periodBar}>
          <span>{t('books.day')}</span>

          <Input
            className={styles.headerControl}

            type="date"

            required

            value={day}

            onChange={(e) => setDay(e.target.value)}
          />
        </label>
      )}
      {group ? (
        month && <Group month={month} />
      ) : tab === 'daily' ? (
        day && <DailyClosing day={day} />
      ) : tab === 'revenue' ? (
        month && <RevenueReport month={month} />
      ) : tab === 'claims' ? (
        <ClaimsReport />
      ) : tab === 'stock' ? (
        month && <StockReport month={month} />
      ) : tab === 'quality' ? (
        <QualityReport />
      ) : (
        month && <AntibioticsReport month={month} />
      )}
    </PageContainer>
  );
}
function Group({ month }: { month: string }) {
  const { t } = useTranslation();
  const q = useReport<GroupReport>('group', `?month=${month}`);
  const rows = q.data?.rows;
  return (
    <>
      {q.data && (
        <div className={dash.tiles}>
          <StatTile
            label={t('operations.visits')}
            value={rows!.reduce((s, r) => s + r.visits, 0)}
            caption={month}
          />
          <StatTile
            label={t('operations.revenue')}
            value={formatRM(rows!.reduce((s, r) => s + r.revenueSen, 0))}
            caption={month}
          />
          <StatTile
            label="Booked"
            value={pct(
              rows!.reduce((s, r) => s + r.bookedVisits, 0),
              rows!.reduce((s, r) => s + r.visits, 0),
            )}
            caption="Visits that came from a booking"
          />
          {/* The API gives adoption per branch only: a patient seen at
              both branches counts in each, so this is the sum of branches. */}
          <StatTile
            label={`${t('operations.adoption')} (sum of branches)`}
            value={pct(
              rows!.reduce((s, r) => s + r.appPatients, 0),
              rows!.reduce((s, r) => s + r.patientsSeen, 0),
            )}
            caption="Patients with the app, branches added up"
          />
        </div>
      )}
      <Card
        title={t('reports.byBranch')}
        flush
        actions={
          // RL-1827: the same report as a CSV file.
          <FileButton
            path={`/reports/group/csv?month=${encodeURIComponent(month)}`}
            name={`group-report-${month}.csv`}
            label="Download CSV"
          />
        }
      >
        <DataTable
          caption={t('operations.groupReports')}
          rows={rows}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(r) => r.branch}
          empty={<EmptyState title={t('operations.noRecords')} />}
          columns={[
            {
              key: 'branch',
              header: t('operations.branch'),
              render: (r) => <strong>{r.branch}</strong>,
            },
            {
              key: 'visits',
              header: t('operations.visits'),
              align: 'right',
              render: (r) => r.visits,
            },
            {
              key: 'booked',
              header: 'Booked',
              align: 'right',
              render: (r) => (
                <>
                  {`${r.bookedPct}%`}
                  <span className={styles.sub}>
                    {r.bookedVisits} of {r.visits}
                  </span>
                </>
              ),
            },
            {
              key: 'revenue',
              header: t('operations.revenue'),
              align: 'right',
              render: (r) => formatRM(r.revenueSen),
            },
            {
              key: 'panel',
              header: t('reports.panelShare'),
              render: (r) =>
                r.panelSharePct === undefined ? '—' : `${r.panelSharePct}%`,
            },
            {
              key: 'wait',
              header: t('operations.wait'),
              render: (r) =>
                r.avgWaitMinutes === null ? '—' : `${r.avgWaitMinutes} min`,
            },
            {
              key: 'app',
              header: t('operations.adoption'),
              render: (r) =>
                r.appAdoptionPct === null ? (
                  '—'
                ) : (
                  <>
                    {`${r.appAdoptionPct}%`}
                    <span className={styles.sub}>
                      {r.appPatients} of {r.patientsSeen} patients
                    </span>
                  </>
                ),
            },
            {
              key: 'outstanding',
              header: 'Outstanding',
              align: 'right',
              render: (r) => (
                <>
                  {formatRM(r.panelOutstandingSen + r.patientOutstandingSen)}
                  <span className={styles.sub}>
                    panels {formatRM(r.panelOutstandingSen)} · patients{' '}
                    {formatRM(r.patientOutstandingSen)}
                  </span>
                </>
              ),
            },
          ]}
        />
        <p className={styles.cardFoot}>
          Outstanding is as of today: panel invoices issued and not fully paid,
          and patients&apos; open bill balances.
        </p>
      </Card>
      {q.data && (
        <Card title="By service">
          {q.data.serviceMix.length ? (
            <HBars
              rows={q.data.serviceMix.map((s) => ({
                label: s.service,
                value: s.visits,
                display: `${s.sharePct} %`,
                extra: <span className={styles.sub}>{s.visits} visits</span>,
              }))}
            />
          ) : (
            <EmptyState title={t('operations.noRecords')} />
          )}
          <span className={styles.sub}>
            The same visits as the branch table, by service, in {q.data.month}{' '}
            (video consults included).
          </span>
        </Card>
      )}
    </>
  );
}
function DailyClosing({ day }: { day: string }) {
  const { t } = useTranslation();
  const q = useReport<Daily>('daily-closing', `?day=${day}`);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <>
      <div className={dash.tiles}>
        <StatTile
          label={t('reports.visitsToday')}
          value={d.visits}
          caption={d.day}
        />
        <StatTile
          label={t('operations.netCollections')}
          value={formatRM(d.netSen)}
          caption={t('reports.afterRefunds')}
        />
        <StatTile
          label={t('operations.openBills')}
          value={d.openBills}
          caption={formatRM(d.openBalanceSen)}
        />
        <StatTile
          label={t('reports.cashDifference')}
          value={
            d.shifts
              ? formatRM(d.cashDifferenceSen)
              : t('reports.noClosedShift')
          }
        />
      </div>
      <Card title={t('reports.byMethod')}>
        {d.byMethod.length ? (
          <HBars
            rows={d.byMethod.map((r) => ({
              label: METHOD_LABEL[r.method as PaymentMethod] ?? r.method,
              value: r.sen,
              display: formatRM(r.sen),
            }))}
          />
        ) : (
          <EmptyState title={t('operations.noRecords')} />
        )}
        <span className={styles.sub}>
          {t('reports.refunds')}: {formatRM(d.refundsSen)}
        </span>
      </Card>
      <ReportDispatchPanel report="daily_closing" day={day} />
    </>
  );
}
function RevenueReport({ month }: { month: string }) {
  const { t } = useTranslation();
  const q = useReport<Revenue>('revenue', `?month=${month}`);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <>
      <div className={dash.tiles}>
        <StatTile
          label={t('reports.monthToDate')}
          value={formatRM(d.totalMonthSen)}
          caption={month}
        />
        <StatTile
          label={t('reports.yearToDate')}
          value={formatRM(d.totalYtdSen)}
          caption={month}
        />
        {d.byBranch.map((r) => (
          <StatTile key={r.name} label={r.name} value={formatRM(r.monthSen)} />
        ))}
      </div>
      <div className={styles.grid3}>
        {[
          ['byPayer', d.byPayer],
          ['byService', d.byService],
          ['byDoctor', d.byDoctor],
        ].map(([key, rows]) => (
          <Card key={key as string} title={t(`reports.${key as string}`)}>
            {(rows as RevenueRow[]).length ? (
              <HBars
                rows={(rows as RevenueRow[]).map((r) => ({
                  label: r.name,
                  value: r.monthSen,
                  display: formatRM(r.monthSen),
                }))}
              />
            ) : (
              <EmptyState title={t('operations.noRecords')} />
            )}
          </Card>
        ))}
      </div>
    </>
  );
}
function ClaimsReport() {
  const { t } = useTranslation();
  const q = useReport<ClaimsRow[]>('claims', '');
  return (
    <Card title={t('reports.sixMonths')} flush>
      <DataTable
        caption={t('reports.claims')}
        rows={q.data}
        rowKey={(r) => r.payer}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        columns={[
          {
            key: 'payer',
            header: t('reports.panel'),
            render: (r) => <strong>{r.payer}</strong>,
          },
          {
            key: 'claims',
            header: t('reports.claimsCount'),
            align: 'right',
            render: (r) => r.claims,
          },
          {
            key: 'rejected',
            header: t('reports.rejected'),
            render: (r) =>
              r.claims ? `${Math.round((100 * r.rejected) / r.claims)}%` : '—',
          },
          {
            key: 'days',
            header: t('reports.daysToPay'),
            align: 'right',
            render: (r) => r.avgDaysToPay ?? '—',
          },
          {
            key: 'terms',
            header: t('reports.terms'),
            align: 'right',
            render: (r) => r.termsDays,
          },
          {
            key: 'writeoff',
            header: t('reports.writtenOff'),
            align: 'right',
            render: (r) => formatRM(r.writtenOffSen),
          },
          {
            key: 'reason',
            header: t('reports.reason'),
            render: (r) => r.topReason ?? '—',
          },
        ]}
      />
    </Card>
  );
}
function StockReport({ month }: { month: string }) {
  const { t } = useTranslation();
  const q = useReport<Stock>('stock', `?month=${month}`);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <>
      <div className={dash.tiles}>
        <StatTile
          label={t('reports.stockValue')}
          value={formatRM(d.valueSen)}
        />
        <StatTile
          label={t('reports.expiredBatches')}
          value={d.expiredBatches}
          caption={month}
        />
        <StatTile
          label={t('reports.writtenOff')}
          value={formatRM(d.writeOffSen)}
          caption={month}
        />
        <StatTile
          label={t('reports.unitsDispensed')}
          value={d.unitsDispensed}
          caption={month}
        />
      </div>
      <p className={styles.notice}>{t('reports.stockBody')}</p>
      <div className={styles.grid2}>
        {(['fastest', 'slowest'] as const).map((key) => (
          <Card title={t(`reports.${key}`)} key={key} flush>
            <DataTable
              caption={t(`reports.${key}`)}
              rows={d[key]}
              rowKey={(r) => r.name}
              columns={[
                {
                  key: 'name',
                  header: t('reports.medicine'),
                  render: (r) => r.name,
                },
                {
                  key: 'qty',
                  header: t('reports.quantity'),
                  align: 'right',
                  render: (r) => r.qty,
                },
              ]}
            />
          </Card>
        ))}
      </div>
    </>
  );
}

interface Quality {
  diabetes: {
    patients: number;
    withHba1c: number;
    controlled: number;
    target: string;
  };
  hypertension: {
    patients: number;
    withBp: number;
    controlled: number;
    target: string;
  };
  eyeScreening: { due: number; doneLast12Months: number; everyMonths: number };
  notifiableCasesLast12Months: number;
  topDiagnoses: {
    month: string;
    previousMonth: string;
    rows: {
      code: string;
      description: string;
      count: number;
      previous: number;
    }[];
  };
  targetsApproved: boolean;
  targetsSource: string;
}
const pct = (n: number, d: number) =>
  d ? `${Math.round((n * 100) / d)} %` : '—';

/** RL-731 Clinical quality: register control rates against the configured targets. */
function QualityReport() {
  const q = useReport<Quality>('quality', '');
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <>
      {!d.targetsApproved && (
        <p className={styles.notice}>
          <strong>Targets are pending clinical sign-off.</strong>{' '}
          {d.targetsSource}
        </p>
      )}
      <div className={dash.tiles}>
        <StatTile
          label={`Diabetes: ${d.diabetes.target}`}
          value={pct(d.diabetes.controlled, d.diabetes.patients)}
          caption={`${d.diabetes.controlled} of ${d.diabetes.patients} on the register`}
        />
        <StatTile
          label={`Hypertension: ${d.hypertension.target}`}
          value={pct(d.hypertension.controlled, d.hypertension.withBp)}
          caption={`${d.hypertension.controlled} of ${d.hypertension.withBp} with a BP`}
        />
        <StatTile
          label="Eye screening up to date"
          value={pct(d.eyeScreening.doneLast12Months, d.eyeScreening.due)}
          caption={`diabetes register, every ${d.eyeScreening.everyMonths} months`}
        />
        <StatTile
          label="Notifiable cases"
          value={d.notifiableCasesLast12Months}
          caption="last 12 months"
        />
      </div>
      <Card
        title={`Top diagnoses · ${d.topDiagnoses.month} vs ${d.topDiagnoses.previousMonth}`}
        flush
      >
        <DataTable
          caption="Top diagnoses"
          rows={d.topDiagnoses.rows}
          rowKey={(r) => r.code}
          empty={<EmptyState title="No diagnoses recorded this month" />}
          columns={[
            {
              key: 'd',
              header: 'Diagnosis',
              render: (r) => <strong>{`${r.code} ${r.description}`}</strong>,
            },
            {
              key: 'n',
              header: d.topDiagnoses.month,
              align: 'right',
              render: (r) => r.count,
            },
            {
              key: 'p',
              header: d.topDiagnoses.previousMonth,
              align: 'right',
              render: (r) => r.previous,
            },
            {
              key: 'c',
              header: 'Change',
              render: (r) => {
                if (!r.previous) return 'New';
                const c = Math.round(
                  ((r.count - r.previous) * 100) / r.previous,
                );
                return c > 20 ? (
                  <Badge tone="warning">+{c} %</Badge>
                ) : (
                  `${c > 0 ? '+' : ''}${c} %`
                );
              },
            },
          ]}
        />
      </Card>
    </>
  );
}

interface Antibiotics {
  month: string;
  targetMaxPct: number;
  targetApproved: boolean;
  targetSource: string;
  rows: {
    doctor: string;
    urtiVisits: number;
    withAntibiotic: number;
    ratePct: number;
    aboveTarget: boolean;
  }[];
}

/** RL-731 Antibiotics for upper respiratory infections, by doctor. */
function AntibioticsReport({ month }: { month: string }) {
  const q = useReport<Antibiotics>('antibiotics', `?month=${month}`);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <Card
      title={`Antibiotics for upper respiratory infections · ${d.month}`}
      flush
    >
      <DataTable
        caption="Antibiotics for upper respiratory infections"
        rows={d.rows}
        rowKey={(r) => r.doctor}
        empty={
          <EmptyState title="No upper respiratory infection visits this month" />
        }
        columns={[
          {
            key: 'd',
            header: 'Doctor',
            render: (r) => <strong>{r.doctor}</strong>,
          },
          {
            key: 'u',
            header: 'URTI visits',
            align: 'right',
            render: (r) => r.urtiVisits,
          },
          {
            key: 'a',
            header: 'Given an antibiotic',
            align: 'right',
            render: (r) => r.withAntibiotic,
          },
          {
            key: 'r',
            header: 'Rate',
            render: (r) => (
              <>
                <strong>{r.ratePct} %</strong>{' '}
                <Badge tone={r.aboveTarget ? 'warning' : 'success'}>
                  {r.aboveTarget ? 'Review with the doctor' : 'Within target'}
                </Badge>
              </>
            ),
          },
        ]}
      />
      <p className={styles.cardFoot}>
        Most URTIs are viral. Clinic target: under {d.targetMaxPct} %
        {d.targetApproved ? '.' : ' (pending clinical sign-off).'}{' '}
        {d.targetSource}
      </p>
    </Card>
  );
}
